import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from '@atgfr8/atg-ui-tailwind';
import { CloseIcon } from '@atgfr8/icons';
import { Button } from '@components';
import { closeModal, isModalOpen } from '@store/modals';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { createEffect, createSignal, Show, type JSX } from 'solid-js';

export interface BasicModalProps {
  class?: string;
  id: string;
  showModal: boolean;
  children: JSX.Element;
  width?: string;
  maxWidth?: string;
  height?: string;
  onSubmitText?: string;
  onSubmitLoading?: boolean;
  onSubmitDisabled?: boolean;
  onSubmit?: () => void;
  onCloseText?: string;
  onClose?: () => void;
  header?: boolean;
  title?: string;
  footer?: boolean;
  style?: JSX.CSSProperties;
  modalStyles?: Record<string, string>;
  footerContainerClass?: string;
  closeButtonVariant?: 'text' | 'outlined' | 'contained';
  footerChild?: JSX.Element;
  mainContainerStyles?: JSX.CSSProperties;
  showClose?: boolean;
  // Blocks the header X, Escape, and any other Dialog close while true.
  closeDisabled?: boolean;
  fullHeight?: boolean;
  preventCloseOnSubmit?: boolean;
  hideClose?: boolean;
  customizedHeaderContent?: JSX.Element;
  stickyHeaderFooter?: boolean;
  noCloseOnClickOutside?: boolean;
  backdropClass?: string;
}

type ModalPropsT = Omit<BasicModalProps, 'showModal'>;

export const BasicModal = (props: ModalPropsT) => {
  const [isOpen, setIsOpen] = createSignal(isModalOpen(props.id));

  createEffect(() => {
    setIsOpen(isModalOpen(props.id));
  });

  const onModalClose = () => {
    if (props.closeDisabled) {
      return;
    }

    if (props.onClose) {
      props.onClose();
    }

    if (!!props.id) {
      closeModal(props.id);
    }
  };

  const handleSubmit = () => {
    if (props.onSubmitDisabled) {
      return;
    }

    props.onSubmit && props.onSubmit();

    if (!(props.preventCloseOnSubmit ?? false)) {
      onModalClose();
    }
  };

  return (
    <Dialog
      id={props.id}
      class={`w-[25vw] rounded-[5px] bg-white flex max-h-[100vh]! ${
        props.class || ''
      }`}
      onClose={onModalClose}
      open={isOpen()}
      style={{
        width: props.width,
        'max-width': props.maxWidth ?? props.width,
        height: !!props.fullHeight ? 'calc(100vh - 240px)' : props.height,
        ...props.style,
      }}
      noCloseOnClickOutside={props.noCloseOnClickOutside ?? true}
      backdropClass={props.backdropClass}
    >
      <Show when={props.header !== false}>
        <DialogTitle class="flex flex-row">
          <div class="flex-1">
            {props.title}
            <Show when={Boolean(props.customizedHeaderContent)}>
              {props.customizedHeaderContent}
            </Show>
          </div>
          <Show when={!props.hideClose && props.showClose !== false}>
            <div>
              <IconButton
                size="sm"
                disabled={props.closeDisabled}
                on:click={onModalClose}
                dataTestId={formatDataTestId(
                  `Modal ${props.title} Close`,
                  'button',
                )}
              >
                <CloseIcon size={20} color="rgba(0, 0, 0, 0.54)" />
              </IconButton>
            </div>
          </Show>
        </DialogTitle>
      </Show>
      <DialogContent
        class="overflow-auto! flex-1 relative pt-[6px]"
        style={props.mainContainerStyles}
      >
        <Show
          when={
            props.header === false &&
            !props.hideClose &&
            props.showClose !== false
          }
        >
          <div class="absolute top-0 right-0">
            <IconButton
              size="xs"
              disabled={props.closeDisabled}
              on:click={onModalClose}
              dataTestId={formatDataTestId(
                `Modal ${props.title} Close`,
                'button',
              )}
            >
              <CloseIcon size={20} color="rgba(0, 0, 0, 0.54)" />
            </IconButton>
          </div>
        </Show>
        {props.children}
      </DialogContent>
      <Show when={props.footer || !!props.onSubmitText || !!props.onCloseText}>
        <DialogActions class="mb-2 pr-2 gap-2! relative">
          <Show when={!!props.footerChild}>{props.footerChild}</Show>
          <Button
            isLoading={props.onSubmitLoading}
            disabled={props.onSubmitDisabled}
            variant="contained"
            label={props.onSubmitText || 'Submit'}
            type="submit"
            onClick={handleSubmit}
            dataTestId={formatDataTestId(
              `Modal ${props.title} Submit`,
              'button',
            )}
          />
          <Button
            class="float-right"
            label={props.onCloseText || 'Cancel'}
            type="button"
            disabled={props.closeDisabled}
            variant={
              Boolean(props.closeButtonVariant)
                ? props.closeButtonVariant
                : 'outlined'
            }
            onClick={onModalClose}
            dataTestId={formatDataTestId(
              `Modal ${props.title} Cancel`,
              'button',
            )}
          />
        </DialogActions>
      </Show>
    </Dialog>
  );
};
